<template>
  <HrPageLayout
    :title="form?.title || 'Appraisal Form'"
    subtitle="Download the form, complete it, upload, and submit to your supervisor"
    icon="📝"
    :breadcrumb-items="[
      { label: 'My Appraisals', route: '/my-appraisals' },
      { label: 'Fill Form' }
    ]"
  >
    <div v-if="loading" class="hr-loading-wrap hr-py-5"><div class="hr-spinner"></div></div>

    <div v-else-if="!form" class="hr-chart-card hr-text-center hr-py-5">
      <p class="hr-text-muted">Form not found or not assigned to you.</p>
      <button class="hr-btn hr-btn-primary hr-mt-3" @click="$router.push('/my-appraisals')">Back</button>
    </div>

    <div v-else class="fill-layout">
      <div class="hr-chart-card hr-mb-3">
        <div class="hr-chart-body">
          <div class="info-grid">
            <div><span class="label">Period</span><strong>{{ form.appraisalYear }} {{ form.periodLabel }}</strong></div>
            <div><span class="label">Deadline</span><strong>{{ form.submissionDeadline || 'None' }}</strong></div>
            <div><span class="label">Supervisor</span><strong>{{ submission?.supervisor_name || 'Will be assigned on submit' }}</strong></div>
            <div><span class="label">Status</span><span class="hr-badge" :class="statusClass">{{ submission?.status || 'Not Started' }}</span></div>
          </div>
          <p v-if="form.description" class="hr-mt-3 hr-text-muted">{{ form.description }}</p>
          <div v-if="submission?.supervisor_notes && submission.status === 'Returned'" class="return-box hr-mt-3">
            <strong>Supervisor feedback:</strong> {{ submission.supervisor_notes }}
          </div>
        </div>
      </div>

      <div class="steps">
        <div class="step">
          <div class="step-num">1</div>
          <div>
            <h4>Download blank form</h4>
            <p class="hr-text-muted">Open the company PDF, fill it on your device or print and scan.</p>
            <button class="hr-btn hr-btn-outline hr-mt-2" @click="downloadTemplate">
              <i class="bx bx-download"></i> Download PDF
            </button>
          </div>
        </div>
        <div class="step">
          <div class="step-num">2</div>
          <div>
            <h4>Upload completed form</h4>
            <input type="file" accept="application/pdf,.pdf" @change="onFileChange" class="hr-input" :disabled="!canEdit" />
            <p v-if="submission?.completed_pdf_name" class="hr-mt-2">
              Current file: <strong>{{ submission.completed_pdf_name }}</strong>
              <button class="hr-btn hr-btn-sm hr-btn-info hr-ms-2" @click="downloadCompleted">View</button>
            </p>
          </div>
        </div>
        <div class="step">
          <div class="step-num">3</div>
          <div>
            <h4>Comments for supervisor (optional)</h4>
            <textarea v-model="employeeNotes" class="hr-input" rows="4" :disabled="!canEdit"></textarea>
          </div>
        </div>
      </div>

      <div class="actions hr-mt-4">
        <button class="hr-btn hr-btn-secondary" @click="$router.push('/my-appraisals')">Back</button>
        <button v-if="canEdit" class="hr-btn hr-btn-outline" :disabled="saving" @click="saveDraft">Save Draft</button>
        <button v-if="canEdit" class="hr-btn hr-btn-primary" :disabled="saving" @click="submitToSupervisor">
          {{ saving ? 'Submitting...' : 'Submit to Supervisor' }}
        </button>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "EmployeeAppraisalFormFill",
  components: { HrPageLayout },
  data() {
    return {
      loading: true,
      saving: false,
      formId: null,
      form: null,
      submission: null,
      submissionId: null,
      employeeNotes: "",
      completedPdf: null,
    };
  },
  computed: {
    canEdit() {
      const s = this.submission?.status;
      return !s || s === "Draft" || s === "Returned" || s === "Not Started";
    },
    statusClass() {
      const map = {
        Draft: "hr-badge-secondary",
        Submitted: "hr-badge-warning",
        Returned: "hr-badge-info",
        Approved: "hr-badge-success",
        Rejected: "hr-badge-danger",
      };
      return map[this.submission?.status] || "hr-badge-secondary";
    },
  },
  async mounted() {
    this.formId = Number(this.$route.query.formId || 0);
    await this.bootstrap();
  },
  methods: {
    async bootstrap() {
      this.loading = true;
      try {
        const listData = new FormData();
        listData.append("function", "getMyAppraisalPdfForms");
        const listRes = await execute(listData, authUrl);
        const forms = listRes?.data?.data?.forms || [];
        this.form = forms.find((f) => Number(f.id) === this.formId) || null;
        if (!this.form) {
          this.loading = false;
          return;
        }

        if (!this.form.submissionId) {
          const startData = new FormData();
          startData.append("function", "startAppraisalPdfSubmission");
          startData.append("formId", this.formId);
          const startRes = await execute(startData, authUrl);
          this.submissionId = startRes?.data?.data?.submissionId || null;
        } else {
          this.submissionId = this.form.submissionId;
        }

        await this.loadSubmission();
      } catch (e) {
        console.error(e);
        this.$toast?.error("Failed to load appraisal form") || alert("Failed to load appraisal form");
      } finally {
        this.loading = false;
      }
    },
    async loadSubmission() {
      if (!this.submissionId && !this.formId) return;
      const data = new FormData();
      data.append("function", "getAppraisalPdfSubmission");
      if (this.submissionId) data.append("submissionId", this.submissionId);
      else data.append("formId", this.formId);
      const res = await execute(data, authUrl);
      if (res?.data?.success) {
        this.submission = res.data.data?.submission || null;
        this.submissionId = this.submission?.id || this.submissionId;
        this.employeeNotes = this.submission?.employee_notes || "";
        if (res.data.data?.form) {
          this.form = {
            ...this.form,
            title: res.data.data.form.title,
            description: res.data.data.form.description,
            appraisalYear: res.data.data.form.appraisal_year,
            periodLabel: res.data.data.form.period_label,
            submissionDeadline: res.data.data.form.submission_deadline,
          };
        }
      }
    },
    onFileChange(e) {
      this.completedPdf = e.target.files?.[0] || null;
    },
    buildPayload() {
      const data = new FormData();
      data.append("submissionId", this.submissionId);
      data.append("employeeNotes", this.employeeNotes || "");
      if (this.completedPdf) data.append("completedPdf", this.completedPdf);
      return data;
    },
    async saveDraft() {
      this.saving = true;
      try {
        const data = this.buildPayload();
        data.append("function", "saveAppraisalPdfSubmission");
        const res = await execute(data, authUrl);
        if (!res?.data?.success) throw new Error(res?.data?.message || "Save failed");
        this.$toast?.success("Draft saved") || alert("Draft saved");
        await this.loadSubmission();
      } catch (e) {
        this.$toast?.error(e.message) || alert(e.message);
      } finally {
        this.saving = false;
      }
    },
    async submitToSupervisor() {
      if (!this.completedPdf && !this.submission?.completed_pdf_path) {
        this.$toast?.error("Upload your completed PDF first") || alert("Upload your completed PDF first");
        return;
      }
      if (!confirm("Submit this appraisal to your supervisor? You may not be able to edit after submission.")) return;

      this.saving = true;
      try {
        const data = this.buildPayload();
        data.append("function", "submitAppraisalPdfToSupervisor");
        const res = await execute(data, authUrl);
        if (!res?.data?.success) throw new Error(res?.data?.message || "Submit failed");
        this.$toast?.success("Submitted to supervisor") || alert("Submitted to supervisor");
        this.$router.push("/my-appraisals");
      } catch (e) {
        this.$toast?.error(e.message) || alert(e.message);
      } finally {
        this.saving = false;
      }
    },
    async downloadTemplate() {
      const data = new FormData();
      data.append("function", "downloadAppraisalPdfFile");
      data.append("type", "template");
      data.append("formId", this.formId);
      const res = await executeDownload(data, authUrl);
      const blob = new Blob([res.data], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob), "_blank");
    },
    async downloadCompleted() {
      if (!this.submissionId) return;
      const data = new FormData();
      data.append("function", "downloadAppraisalPdfFile");
      data.append("type", "completed");
      data.append("submissionId", this.submissionId);
      const res = await executeDownload(data, authUrl);
      const blob = new Blob([res.data], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob), "_blank");
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; }
.info-grid .label { display: block; font-size: 12px; color: #64748b; margin-bottom: 4px; }
.steps { display: grid; gap: 16px; }
.step { display: flex; gap: 14px; background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 16px; }
.step-num {
  width: 36px; height: 36px; border-radius: 50%; background: #eef2ff; color: #4f46e5;
  display: flex; align-items: center; justify-content: center; font-weight: 700; flex-shrink: 0;
}
.actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; }
.return-box { background: #fff7ed; border: 1px solid #fdba74; border-radius: 10px; padding: 12px; }
.hr-ms-2 { margin-left: 8px; }
</style>
